import { css } from 'aphrodite';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConnectionState, FinancialBrokerDealer, TelephonyServiceProvider } from '../../../../../extViewmodels';
import { useTextMessaging, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getFormattedPhoneNumber, isValidPhone } from '../../../../../models/UiUtils';
import {
	usePhoneNumberUserSuspenseQuery,
	useRemoveZoomPhoneNumberMutation,
	useUserPreferencesMutation,
} from '../../../../../queries';
import { usePhoneNumberCallForwardingMutation } from '../../../../../queries/PhoneNumber/usePhoneNumberCallForwardingMutation';
import { usePhoneNumberDeleteCallForwardingMutation } from '../../../../../queries/PhoneNumber/usePhoneNumberDeleteCallForwardingMutation';
import { Button } from '../../../../components-v2/Button';
import { Input } from '../../../../components-v2/Input';
import { TextArea } from '../../../../components-v2/TextArea';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../components-v2/Dialog';
import { baseStyleSheet } from '../../../../styles/styles';
import { InputFieldError } from '../../../InputFieldError';
import { RadioButton } from '../../../RadioButton';
import { SettingsSubGroup } from '../../SettingsSubGroup';
import { styleSheet } from './styles';
import { ZoomPhoneTexting } from './ZoomPhoneTexting';

interface IProps {
	onCancel?(): void;
}

const formatForwardingPhoneNumber = (value: string) => {
	const digitsOnly = value.replace(/\D/g, '');
	const normalizedDigits = digitsOnly.length === 11 && digitsOnly.startsWith('1') ? digitsOnly.slice(1) : digitsOnly;
	return getFormattedPhoneNumber(normalizedDigits);
};

export const TextingSettings: React.FC<IProps> = observer(({ onCancel }) => {
	const logger = useEventLogging();
	const textMessagingVm = useTextMessaging();
	const userSession = useUserSession();
	const phoneNumberCallForwardingMutation = usePhoneNumberCallForwardingMutation({
		onError: error => {
			logger.logApiError('Call Forwarding-Error', error);
			toast.error(error.systemMessage || 'Failed to update call forwarding');
		},
		onSuccess: () => {
			toast.success('Call forwarding updated');
			textMessagingVm.getUserPhone();
		},
	});
	const phoneNumberDeleteCallForwardingMutation = usePhoneNumberDeleteCallForwardingMutation({
		onError: error => {
			logger.logApiError('Call-Delete-Forwarding-Error', error);
			toast.error(error.systemMessage || 'Failed to delete call forwarding number');
		},
		onSuccess: () => {
			toast.success('Call forwarding disabled');
			textMessagingVm.getUserPhone();
		},
	});
	const userPreferencesMutation = useUserPreferencesMutation({
		onError: error => {
			logger.logApiError('Call Forwarding-Error', error);
			toast.error(error?.systemMessage || 'Failed to update user preferences');
		},
		onSuccess: () => {
			toast.success('Text message signature disclosure setting saved');
		},
	});
	const [isRemoveZoomDialogOpen, setIsRemoveZoomDialogOpen] = useState(false);
	const removeZoomNumberMutation = useRemoveZoomPhoneNumberMutation({
		onError: error => {
			logger.logApiError('RemoveZoomNumber-Error', error);
			toast.error(error.systemMessage || 'Failed to remove the Zoom Phone number');
		},
		onSuccess: () => {
			toast.success('Zoom Phone number removed from texting');
			setIsRemoveZoomDialogOpen(false);
			textMessagingVm.getUserPhone();
		},
	});
	const { data: telephonyConfigurations } = usePhoneNumberUserSuspenseQuery();
	const phoneNumberOrder = telephonyConfigurations?.filter(
		config =>
			config.connectionState !== ConnectionState.Disconnected && config.connectionState !== ConnectionState.Failed
	)?.[0];
	const [forwardingEnabled, setForwardingEnabled] = useState(
		!!phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard
	);

	// Check if the current user owns the number
	const currentUserOwnsNumber = phoneNumberOrder?.creator?.id === userSession?.user?.id;
	const isZoomPhoneNumber = phoneNumberOrder?.provider === TelephonyServiceProvider.ZoomPhone;
	const zoomPhoneEnabled = !!userSession.account.features?.texting?.zoomPhoneEnabled;
	const [forwardingNumberErrorMessage, setForwardingNumberErrorMessage] = useState<string>('');
	const [forwardingPhoneNumber, setForwardingPhoneNumber] = useState('');
	const [userFirstTextMessageSignature, setUserFirstTextMessageSignature] = useState('');

	// This disclosure/signature was requested by Cetera so for now we are only showing it for accounts that have cetera.
	// IF the user has one already set we should also show it so they can remove it.
	const showTextDisclosure =
		userSession.account.additionalInfo.brokerDealer === FinancialBrokerDealer.Cetera ||
		userSession?.user?.userPreferences?.templatePreferences?.firstTextMessageSignature;

	const onSave = async () => {
		const trimmedForwardingPhoneNumber = forwardingPhoneNumber.trim();
		const hasValidForwardingPhoneNumber =
			trimmedForwardingPhoneNumber !== '' && isValidPhone(trimmedForwardingPhoneNumber, true);
		const formattedForwardingPhoneNumber = hasValidForwardingPhoneNumber
			? formatForwardingPhoneNumber(trimmedForwardingPhoneNumber)
			: '';

		// Only handle call forwarding if the current user owns the number and it's not a hosted number
		if (currentUserOwnsNumber && !isZoomPhoneNumber && !phoneNumberOrder?.isHosted) {
			if (!forwardingEnabled) {
				phoneNumberDeleteCallForwardingMutation.mutate({
					id: phoneNumberOrder.id,
				});
			} else if (hasValidForwardingPhoneNumber) {
				phoneNumberCallForwardingMutation.mutate({
					id: phoneNumberOrder.id,
					number: formattedForwardingPhoneNumber,
				});
			}
		}

		if (showTextDisclosure) {
			const body = userSession.user.userPreferences;
			runInAction(() => {
				body.templatePreferences = {
					...body.templatePreferences,
					firstTextMessageSignature: userFirstTextMessageSignature,
				};
			});
			userPreferencesMutation.mutate({
				preferences: body,
			});
		}
	};

	useEffect(() => {
		if (phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard) {
			setForwardingEnabled(true);
		}
	}, [phoneNumberOrder]);

	// #region call forwarding callbacks
	const enableForwarding = () => {
		if (!forwardingEnabled) {
			setForwardingEnabled(true);
		}
	};

	const disableForwarding = () => {
		if (forwardingEnabled) {
			setForwardingEnabled(false);
			setForwardingPhoneNumber('');
			setForwardingNumberErrorMessage('');
		}
	};

	const onForwardingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setForwardingPhoneNumber(e.target.value || '');
		setForwardingNumberErrorMessage('');
	};

	const onUserFirstTextInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value || '';
		setUserFirstTextMessageSignature(value);
	};

	const onForwardingNumberInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = (e.target.value || '').trim();
		if (!value) {
			setForwardingPhoneNumber('');
			setForwardingNumberErrorMessage('');
			return;
		}
		if (!isValidPhone(value, true)) {
			setForwardingNumberErrorMessage('Please enter a valid phone number');
			return;
		}
		setForwardingPhoneNumber(formatForwardingPhoneNumber(value));
		setForwardingNumberErrorMessage('');
	};
	// #endregion

	const onCancelClick = () => {
		if (currentUserOwnsNumber) {
			setForwardingPhoneNumber('');
			setForwardingNumberErrorMessage('');
			setForwardingEnabled(!!phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard);
		}
		// Close the accordion when Cancel is clicked
		onCancel?.();
	};

	const accountName = userSession.account.companyName;
	const newNumberMailto = `mailto:support@levitateapp.com?subject=${encodeURIComponent(
		accountName ? `New texting number request - ${accountName}` : 'New texting number request'
	)}`;
	const callForwardingMailto = `mailto:support@levitateapp.com?subject=${encodeURIComponent(
		accountName ? `Call forwarding inquiry - ${accountName}` : 'Call forwarding inquiry'
	)}`;

	if (!phoneNumberOrder?.number) {
		return zoomPhoneEnabled ? (
			<div className={css(styleSheet.sections)}>
				<SettingsSubGroup title='Text through Zoom Phone'>
					<ZoomPhoneTexting onImported={() => textMessagingVm.getUserPhone()} />
				</SettingsSubGroup>
			</div>
		) : null;
	}

	return (
		<>
			<div className={css(styleSheet.sections)}>
				<SettingsSubGroup title='General'>
					<div>
						{isZoomPhoneNumber ? (
							<>
								<span>
									Your texting number is {getFormattedPhoneNumber(phoneNumberOrder?.number) || ' '} through Zoom Phone.
									Calls to it ring in Zoom, so call forwarding is managed there.
								</span>
								<div className={css(baseStyleSheet.mt2)}>
									<Button variant='destructive' size='sm' onClick={() => setIsRemoveZoomDialogOpen(true)}>
										Stop texting through Zoom
									</Button>
								</div>
							</>
						) : (
							<>
								<span>Your Levitate texting number is {getFormattedPhoneNumber(phoneNumberOrder?.number) || ' '}</span>
								<span className={css(styleSheet.contactSupport)}>
									<a className={css(styleSheet.supportLink)} href={newNumberMailto}>
										Contact support
									</a>{' '}
									to request a new number
								</span>
							</>
						)}
					</div>
				</SettingsSubGroup>
				{currentUserOwnsNumber && !isZoomPhoneNumber && phoneNumberOrder?.isHosted && (
					<SettingsSubGroup title='Call Forwarding'>
						<div className={css(styleSheet.hostedMessage)}>
							Call Forwarding is not available for this number.
							<br />
							This is a hosted number with texting capabilities only.
							<br />
							<a className={css(styleSheet.supportLink)} href={callForwardingMailto}>
								Contact support
							</a>{' '}
							for more information.
						</div>
					</SettingsSubGroup>
				)}
				{currentUserOwnsNumber && !isZoomPhoneNumber && !phoneNumberOrder?.isHosted && (
					<SettingsSubGroup title='Call Forwarding - Designate a number to dial if someone were to call the number above'>
						<div>
							<RadioButton
								className={css(styleSheet.marginBottom)}
								id='userTextingEnablementEnable'
								name='userTextingEnablement'
								onChange={enableForwarding}
								checked={forwardingEnabled}
							>
								<div className={css(styleSheet.enableForwardingContainer)}>
									<span>Enable Call Forwarding:</span>
									<InputFieldError
										className={css(styleSheet.fieldErrorContainer)}
										errorMessage={forwardingNumberErrorMessage}
									>
										<Input
											styles={[styleSheet.callForwardingInput]}
											onChange={onForwardingInputChange}
											disabled={!forwardingEnabled}
											id='call-forwarding-phone-number'
											type='text'
											onBlur={onForwardingNumberInputBlur}
											placeholder={
												forwardingEnabled ? phoneNumberOrder?.callForwardingNumber?.phoneNumber?.standard : ''
											}
											value={forwardingPhoneNumber}
										/>
									</InputFieldError>
								</div>
							</RadioButton>
							<RadioButton
								id='userTExtingEnablementDisable'
								name='userTextingEnablement'
								onChange={disableForwarding}
								checked={!forwardingEnabled}
							>
								Disable Call Forwarding
							</RadioButton>
						</div>
					</SettingsSubGroup>
				)}
				{showTextDisclosure ? (
					<SettingsSubGroup title='First Text Message Signature and Disclosure'>
						<div>
							<TextArea
								styles={[styleSheet.textSignatureDisclosureInput]}
								id='userFirstTextMessageSignature'
								onChange={onUserFirstTextInputChange}
								value={
									userFirstTextMessageSignature ||
									userSession?.user?.userPreferences?.templatePreferences?.firstTextMessageSignature ||
									''
								}
							/>
						</div>
					</SettingsSubGroup>
				) : null}
			</div>
			<div className={css(baseStyleSheet.mt5, baseStyleSheet.horizontalStack)}>
				<Button
					disabled={
						phoneNumberCallForwardingMutation.isPending ||
						phoneNumberDeleteCallForwardingMutation.isPending ||
						userPreferencesMutation.isPending
					}
					onClick={onSave}
				>
					Save
				</Button>
				<Button onClick={onCancelClick} variant='secondary'>
					Cancel
				</Button>
			</div>
			<Dialog open={isRemoveZoomDialogOpen} onOpenChange={setIsRemoveZoomDialogOpen}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Stop texting through Zoom?</DialogTitle>
						<DialogDescription>
							Levitate will no longer send or show texts for {getFormattedPhoneNumber(phoneNumberOrder?.number)}. The
							number and its history stay in Zoom, and you can add it back any time.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant='secondary'
							disabled={removeZoomNumberMutation.isPending}
							onClick={() => setIsRemoveZoomDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button
							variant='destructive'
							isLoading={removeZoomNumberMutation.isPending}
							onClick={() => removeZoomNumberMutation.mutate({ id: phoneNumberOrder.id })}
						>
							Stop texting through Zoom
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
});
